// Servicio de Maquinaria conectado al backend (máquinas, vehículos, eventos).
// Horómetros, kilómetros y mantenimientos siguen sin backend; devuelven datos vacíos.

import api from './api';

function handleError(error) {
  const message = error.response?.data?.message
    || (error.response?.data?.errors ? Object.values(error.response.data.errors).flat().join(', ') : null)
    || error.message
    || 'Error de conexión';
  return { success: false, message };
}

// Evitar doble petición form-data cuando React Strict Mode monta el componente dos veces
let formDataPending = null;
let formDataPendingKey = null;

export const machineryService = {
  /**
   * Carga en una sola petición todos los datos para formularios de maquinaria:
   * máquinas, vehículos, conductores activos, tipos de evento.
   * Si se pasa eventId, incluye también el evento (para formulario de edición).
   * Reutiliza la petición en curso si se llama dos veces seguidas con los mismos params (ej. Strict Mode).
   */
  async getMachineryFormData(params = {}) {
    const key = JSON.stringify(params);
    if (formDataPendingKey === key && formDataPending) {
      return formDataPending;
    }
    formDataPendingKey = key;
    formDataPending = (async () => {
      try {
        const response = await api.get('/machinery/form-data', { params });
        return response.data;
      } catch (error) {
        return handleError(error);
      } finally {
        formDataPending = null;
        formDataPendingKey = null;
      }
    })();
    return formDataPending;
  },

  /**
   * Cuadro de presentación de eventos: en una sola petición devuelve eventos (con filtros), máquinas y vehículos.
   * Params: dateFrom, dateTo, machineId, vehicleId (opcionales).
   */
  async getEventsListData(params = {}) {
    try {
      const response = await api.get('/machinery/events-list-data', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Máquinas
  async getMachines(params = {}) {
    try {
      const response = await api.get('/machinery/machines', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async getMachine(id) {
    try {
      const response = await api.get(`/machinery/machines/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { success: false, message: 'Máquina no encontrada' };
      }
      return handleError(error);
    }
  },

  async createMachine(data) {
    try {
      const response = await api.post('/machinery/machines', data);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async updateMachine(id, data) {
    try {
      const response = await api.put(`/machinery/machines/${id}`, data);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async deleteMachine(id) {
    try {
      const response = await api.delete(`/machinery/machines/${id}`);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Vehículos
  async getVehicles(params = {}) {
    try {
      const response = await api.get('/machinery/vehicles', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async getVehicle(id) {
    try {
      const response = await api.get(`/machinery/vehicles/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { success: false, message: 'Vehículo no encontrado' };
      }
      return handleError(error);
    }
  },

  async createVehicle(data) {
    try {
      const response = await api.post('/machinery/vehicles', data);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async updateVehicle(id, data) {
    try {
      const response = await api.put(`/machinery/vehicles/${id}`, data);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async deleteVehicle(id) {
    try {
      const response = await api.delete(`/machinery/vehicles/${id}`);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Categorías de maquinaria (líneas separadas de tipo)
  async getCategories(params = {}) {
    try {
      const response = await api.get('/machinery/categories', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async createCategory(data) {
    try {
      const response = await api.post('/machinery/categories', data);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async updateCategory(id, data) {
    try {
      const response = await api.put(`/machinery/categories/${id}`, data);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async deleteCategory(id) {
    try {
      const response = await api.delete(`/machinery/categories/${id}`);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Eventos (bitácora)
  async getEvents(filters = {}) {
    try {
      const params = {};
      if (filters.machineId) params.machineId = filters.machineId;
      if (filters.vehicleId) params.vehicleId = filters.vehicleId;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.event_type) params.event_type = Array.isArray(filters.event_type) ? filters.event_type.join(',') : filters.event_type;
      const response = await api.get('/machinery/events', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async getEvent(id) {
    try {
      const response = await api.get(`/machinery/events/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return { success: false, message: 'Evento no encontrado' };
      }
      return handleError(error);
    }
  },

  async createEvent(data) {
    try {
      const response = await api.post('/machinery/events', data);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async updateEvent(id, data) {
    try {
      const response = await api.put(`/machinery/events/${id}`, data);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async deleteEvent(id) {
    try {
      const response = await api.delete(`/machinery/events/${id}`);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Horómetros (sin backend: listas vacías, operaciones no disponibles)
  async getHourMeters(machineId = null) {
    return { success: true, data: [] };
  },

  async createHourMeter() {
    return { success: false, message: 'Módulo de horómetros no disponible en el backend aún' };
  },

  async updateHourMeter() {
    return { success: false, message: 'Módulo de horómetros no disponible en el backend aún' };
  },

  async deleteHourMeter() {
    return { success: false, message: 'Módulo de horómetros no disponible en el backend aún' };
  },

  // Kilómetros (sin backend)
  async getKilometers(vehicleId = null) {
    return { success: true, data: [] };
  },

  async createKilometer() {
    return { success: false, message: 'Módulo de kilometraje no disponible en el backend aún' };
  },

  async updateKilometer() {
    return { success: false, message: 'Módulo de kilometraje no disponible en el backend aún' };
  },

  async deleteKilometer() {
    return { success: false, message: 'Módulo de kilometraje no disponible en el backend aún' };
  },

  // Alertas de mantenimiento (por km en vehículos y por horas en máquinas)
  async getMaintenanceAlerts() {
    try {
      const response = await api.get('/machinery/maintenance-alerts');
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async getComplianceAlerts() {
    try {
      const response = await api.get('/machinery/compliance-alerts');
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async getVehicleCompliance(vehicleId) {
    try {
      const response = await api.get(`/machinery/vehicles/${vehicleId}/compliance`);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  async createVehicleComplianceRecord(vehicleId, data) {
    try {
      const response = await api.post(`/machinery/vehicles/${vehicleId}/compliance`, data);
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },

  // Reportes de maquinaria (gasto por vehículo, costo por hora por concepto, etc.)
  async getReportDashboard(params) {
    try {
      const response = await api.get('/machinery/reports/dashboard', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  async getReportSpendByVehicle(params) {
    try {
      const response = await api.get('/machinery/reports/spend-by-vehicle', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  async getReportSpendByMachine(params) {
    try {
      const response = await api.get('/machinery/reports/spend-by-machine', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  async getReportCostByEventTypeAndConcept(params) {
    try {
      const response = await api.get('/machinery/reports/cost-by-event-type-and-concept', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  async getReportCostByEventTypeAndConceptGrouped(params) {
    try {
      const response = await api.get('/machinery/reports/cost-by-event-type-and-concept-grouped', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  async getReportSpendByVehicleAndConcept(params) {
    try {
      const response = await api.get('/machinery/reports/spend-by-vehicle-and-concept', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  async getReportUsageAndSpendByVehicle(params) {
    try {
      const response = await api.get('/machinery/reports/usage-and-spend-by-vehicle', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  async getReportSpendByLine(params) {
    try {
      const response = await api.get('/machinery/reports/spend-by-line', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
  async getReportMonetaryInconsistencies(params) {
    try {
      const response = await api.get('/machinery/reports/monetary-inconsistencies', { params });
      return response.data;
    } catch (error) {
      return handleError(error);
    }
  },
};
